/**
 * Survives checkout remount after SPA history POP + restoreLeft (MOB-05).
 * Local React step state is lost when goBack briefly lands on shop; this keeps
 * collect↔payment↔QR ladder honest across that remount.
 *
 * Live `qr` = user is currently on the QR step (`handleQr` write). Remount does
 * **not** keep stale `qr` for auto-QR: Policy A demotes persisted `qr` → UI
 * `payment` + rewrite in-flow `payment` + clear `falseCollectForce` (same end
 * state as leave-off / Back via `persistCheckoutQrDemoteToPayment`).
 * `collect` | `payment` = intentional Back / demote — suppress auto-promote of
 * AWAITING_PAYMENT into the QR step after remount (live Back×2 / restoreLeft).
 * Resume banner still shows from session AWAITING_PAYMENT (not React pendingQr);
 * Resume POSTs start-qr for the live DTO. `false-collect` sessionStorage
 * discriminates cold unusable→collect (may promote on usable bind) from
 * intentional Back→collect (promote-only-when-forced).
 *
 * Cart / Organization chrome leave does not call popCheckoutStepBack. Persist
 * `payment` synchronously on chrome click via persistCheckoutLeaveOffQrFromSearch
 * (G1). Checkout unmount microtask is backup only — same-turn remount can epoch-skip
 * that write so live `handleQr` in-flow `qr` is not undone by StrictMode cleanup.
 * Pay / Resume POSTs start-qr (no auto-jump from remount alone).
 */

export type CheckoutInFlowUiStep = 'collect' | 'payment' | 'qr';

const KEY_PREFIX = 'customer:checkout-in-flow-step:';
/** Set when restore forced collect because session collect was known-unusable. */
const FALSE_COLLECT_KEY_PREFIX = 'customer:checkout-false-collect:';

function storageKey(sessionId: string): string {
  return `${KEY_PREFIX}${sessionId}`;
}

function falseCollectStorageKey(sessionId: string): string {
  return `${FALSE_COLLECT_KEY_PREFIX}${sessionId}`;
}

export function readCheckoutInFlowStep(sessionId: string): CheckoutInFlowUiStep | null {
  try {
    const raw = window.sessionStorage.getItem(storageKey(sessionId));
    if (raw === 'collect' || raw === 'payment' || raw === 'qr') {
      return raw;
    }
    return null;
  } catch {
    return null;
  }
}

export function writeCheckoutInFlowStep(
  sessionId: string,
  step: CheckoutInFlowUiStep,
): void {
  try {
    window.sessionStorage.setItem(storageKey(sessionId), step);
  } catch {
    /* private mode */
  }
}

export function clearCheckoutInFlowStep(sessionId: string): void {
  try {
    window.sessionStorage.removeItem(storageKey(sessionId));
  } catch {
    /* private mode */
  }
  clearCheckoutFalseCollectForce(sessionId);
}

/**
 * True when in-flow `collect` was forced because collect evidence was unusable
 * (cold false-collect). Intentional Back→collect must clear this so remount
 * does not promote to payment under usable NOW (MOB-05).
 */
export function readCheckoutFalseCollectForce(sessionId: string): boolean {
  try {
    return window.sessionStorage.getItem(falseCollectStorageKey(sessionId)) === '1';
  } catch {
    return false;
  }
}

export function markCheckoutFalseCollectForce(sessionId: string): void {
  try {
    window.sessionStorage.setItem(falseCollectStorageKey(sessionId), '1');
  } catch {
    /* private mode */
  }
}

export function clearCheckoutFalseCollectForce(sessionId: string): void {
  try {
    window.sessionStorage.removeItem(falseCollectStorageKey(sessionId));
  } catch {
    /* private mode */
  }
}

/**
 * True when persisted in-flow step means "stay off auto-resumed QR".
 * Remount Policy A demotes stale `qr` → `payment` in cold restore before this
 * is read, so suppress applies after demote; `qr` here means live QR (not remount).
 */
export function shouldSuppressQrAutoResumeFromInFlowStep(
  step: CheckoutInFlowUiStep | null,
): boolean {
  return step === 'collect' || step === 'payment';
}

/**
 * Session id from checkout `?sessionId=` (URL search). Used when chrome leave
 * must persist without rewriting cart/org globally.
 */
export function readCheckoutSessionIdFromSearch(search: string): string | null {
  const query = search.startsWith('?') ? search.slice(1) : search;
  const sessionId = new URLSearchParams(query).get('sessionId');
  if (sessionId === null || sessionId.length === 0) {
    return null;
  }
  return sessionId;
}

/**
 * Intentional in-flow collect|payment persist (Continue / Back): write step and
 * clear false-collect force so remount does not treat it as cold false-collect.
 * Same path as `setCheckoutStepPersisted` for collect|payment (G2).
 */
export function persistCheckoutInFlowStepClearingFalseCollect(
  sessionId: string,
  step: 'collect' | 'payment',
): void {
  writeCheckoutInFlowStep(sessionId, step);
  clearCheckoutFalseCollectForce(sessionId);
}

/**
 * Shared QR → payment demote: write in-flow `payment` and clear false-collect
 * force so remount does not treat leave-off / Back as cold false-collect (G1/G3).
 */
export function persistCheckoutQrDemoteToPayment(sessionId: string): void {
  persistCheckoutInFlowStepClearingFalseCollect(sessionId, 'payment');
}

/**
 * Same persist as popCheckoutStepBack when leaving the QR step via cart /
 * Organization (or any checkout unmount). Demotes `qr` → `payment` so remount
 * suppresses stub auto-QR (MOB-05 / G6). Leaves collect/payment unchanged
 * unless the QR step is still active.
 */
export function persistCheckoutLeaveOffQrStep(
  sessionId: string,
  options?: { readonly qrStepActive?: boolean },
): void {
  if (options?.qrStepActive === true || readCheckoutInFlowStep(sessionId) === 'qr') {
    persistCheckoutQrDemoteToPayment(sessionId);
  }
}

/**
 * Chrome cart / Organization leave: read `?sessionId=` and demote `qr` → `payment`.
 * Must run synchronously on click (before navigate). Unmount `queueMicrotask` +
 * `checkoutLeaveOffQrEpoch` can skip the leave write when checkout remounts in
 * the same turn (G1).
 */
export function persistCheckoutLeaveOffQrFromSearch(search: string): void {
  const sessionId = readCheckoutSessionIdFromSearch(search);
  if (sessionId === null) {
    return;
  }
  persistCheckoutLeaveOffQrStep(sessionId);
}
